import React, { useState, useEffect, useMemo } from 'react';
import { Card, Select, DatePicker, Spin, Empty } from 'antd';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import dayjs from 'dayjs';
import { makePostCall } from '../utils/helper';

const { RangePicker } = DatePicker;

interface ToolData {
  [toolName: string]: number;
}

interface CategoryData {
  category: string;
  tools: ToolData;
}

interface ChartDataPoint {
  category: string;
  [toolName: string]: number | string;
}

interface ApiResponse {
  success: boolean;
  data: CategoryData[];
  metadata: {
    totalRecords: number;
    uniqueCategories: number;
    uniqueTools: number;
    toolsList: string[];
    displayedCategories: number;
    allCategoriesCount: number;
    sortBy: string;
    limit: number;
  };
}

interface FilterOptions {
  userId?: string;
  dateRange?: [dayjs.Dayjs, dayjs.Dayjs] | null;
  modality?: string;
  bodyPart?: string;
  sortBy?: 'toolUsage' | 'category' | 'date';
  limit?: number;
}

// Define distinct colors for different tools
const TOOL_COLORS: { [key: string]: string } = {
  Bidirectional: '#8884d8',
  Crosshairs: '#82ca9d',
  StackScroll: '#ffc658',
  resetViewport: '#ff7c7c',
  WindowLevel: '#a4de6c',
  Zoom: '#d0ed57',
  Pan: '#83a6ed',
  Length: '#8dd1e1',
  Angle: '#d88488',
  EllipticalROI: '#c4a4de',
  RectangleROI: '#f4a582',
  Probe: '#92c5de',
};

// Function to get color for a tool, with fallback to generated colors
const getToolColor = (toolName: string, index: number): string => {
  if (TOOL_COLORS[toolName]) {
    return TOOL_COLORS[toolName];
  }
  // Generate a color based on index if not predefined
  const hue = (index * 137.5) % 360; // Golden angle for better distribution
  return `hsl(${hue}, 70%, 60%)`;
};

const ToolUsageChart: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [toolsList, setToolsList] = useState<string[]>([]);
  const [metadata, setMetadata] = useState<ApiResponse['metadata'] | null>(null);
  const [filters, setFilters] = useState<FilterOptions>({
    dateRange: [dayjs().subtract(7, 'days'), dayjs()],
    sortBy: 'toolUsage',
    limit: 10,
  });
  const [userOptions, setUserOptions] = useState<{ label: string; value: string }[]>([]);

  // Transform API data to chart format
  const transformData = (apiData: CategoryData[]): ChartDataPoint[] => {
    return apiData.map((item) => {
      const dataPoint: ChartDataPoint = {
        category: item.category,
      };

      // Add each tool's count to the data point
      Object.entries(item.tools).forEach(([toolName, count]) => {
        dataPoint[toolName] = count;
      });

      return dataPoint;
    });
  };

  // Fetch users list
  const fetchUsers = async () => {
    try {
      const response = await makePostCall('/no-auth/user-list', {});
      const users = response.data?.data || [];
      setUserOptions(
        users.map((user: any) => ({
          label: user.user_fullname || user.username,
          value: user.username,
        }))
      );
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // Fetch chart data
  const fetchChartData = async () => {
    setLoading(true);
    try {
      const payload: any = {
        sortBy: filters.sortBy || 'toolUsage',
        limit: filters.limit || 10,
      };

      if (filters.userId) {
        payload.userId = filters.userId;
      }

      if (filters.dateRange) {
        payload.startDate = filters.dateRange[0].format('YYYYMMDD');
        payload.endDate = filters.dateRange[1].format('YYYYMMDD');
      }

      if (filters.modality) {
        payload.modality = filters.modality;
      }

      if (filters.bodyPart) {
        payload.bodyPart = filters.bodyPart;
      }

      // Replace '/tool-usage-analytics' with your actual API endpoint
      const response: { data: ApiResponse } = await makePostCall('/no-auth/tool-usage-analytics', payload);

      if (response.data.success) {
        const transformedData = transformData(response.data.data);
        setChartData(transformedData);
        setToolsList(response.data.metadata.toolsList);
        setMetadata(response.data.metadata);
      }
    } catch (error) {
      console.error('Error fetching chart data:', error);
      setChartData([]);
      setToolsList([]);
      setMetadata(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // fetchUsers();
  }, []);

  useEffect(() => {
    fetchChartData();
  }, [filters]);

  const handleFilterChange = (key: keyof FilterOptions, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Custom tooltip for better UX
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            backgroundColor: '#ffffff',
            border: '1px solid #ccc',
            borderRadius: '4px',
            padding: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
        >
          <p style={{ margin: 0, fontWeight: 600, marginBottom: '8px', color: '#000' }}>{label}</p>
          {payload.map((entry: any, index: number) => (
            <p
              key={`item-${index}`}
              style={{
                margin: '4px 0',
                color: entry.color,
                fontSize: '13px',
              }}
            >
              {entry.name}: <strong>{entry.value}</strong>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const isEmpty = !loading && chartData.length === 0;

  return (
    <Card
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
          <div style={{ fontSize: '18px', fontWeight: 600 }}>
            Tool Usage by Modality & Body Part
          </div>
          {metadata && (
            <div style={{ fontSize: '13px', fontWeight: 400, color: '#666' }}>
              Showing {metadata.displayedCategories} of {metadata.allCategoriesCount} categories
            </div>
          )}
        </div>
      }
      style={{
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}
    >
      {/* Filters Section */}
      <div
        style={{
          display: 'flex',
          gap: '16px',
          marginBottom: '24px',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          padding: '16px',
          background: '#fafafa',
          borderRadius: '6px',
          border: '1px solid #e8e8e8',
        }}
      >
        <div style={{ minWidth: '300px' }}>
          <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 500 }}>
            Date Range
          </label>
          <RangePicker
            style={{ width: '100%' }}
            value={filters.dateRange}
            onChange={(dates) => handleFilterChange('dateRange', dates)}
            format="YYYY-MM-DD"
          />
        </div>

        <div style={{ minWidth: '150px' }}>
          <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 500 }}>
            Modality (Optional)
          </label>
          <Select
            placeholder="Select Modality"
            allowClear
            style={{ width: '100%' }}
            value={filters.modality}
            onChange={(value) => handleFilterChange('modality', value)}
            options={[
              { label: 'CT', value: 'CT' },
              { label: 'MR', value: 'MR' },
              { label: 'US', value: 'US' },
              { label: 'XR', value: 'XR' },
              { label: 'DX', value: 'DX' },
            ]}
          />
        </div>

        <div style={{ minWidth: '150px' }}>
          <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 500 }}>
            Body Part (Optional)
          </label>
          <Select
            placeholder="Select Body Part"
            allowClear
            style={{ width: '100%' }}
            value={filters.bodyPart}
            onChange={(value) => handleFilterChange('bodyPart', value)}
            options={[
              { label: 'HEAD', value: 'HEAD' },
              { label: 'CHEST', value: 'CHEST' },
              { label: 'ABDOMEN', value: 'ABDOMEN' },
              { label: 'PELVIS', value: 'PELVIS' },
              { label: 'SPINE', value: 'SPINE' },
              { label: 'EXTREMITY', value: 'EXTREMITY' },
            ]}
          />
        </div>

        <div style={{ minWidth: '180px', marginLeft: 'auto' }}>
          <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 500 }}>
            Sort By
          </label>
          <Select
            style={{ width: '100%' }}
            value={filters.sortBy}
            onChange={(value) => handleFilterChange('sortBy', value)}
            options={[
              { label: '🔥 Most Used First', value: 'toolUsage' },
              { label: '🔤 Alphabetical', value: 'category' },
              { label: '📅 Chronological', value: 'date' },
            ]}
          />
        </div>

        <div style={{ minWidth: '150px' }}>
          <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 500 }}>
            Show Categories
          </label>
          <Select
            style={{ width: '100%' }}
            value={filters.limit}
            onChange={(value) => handleFilterChange('limit', value)}
            options={[
              { label: 'Top 5', value: 5 },
              { label: 'Top 10', value: 10 },
              { label: 'Top 15', value: 15 },
              { label: 'Top 20', value: 20 },
              { label: 'Top 30', value: 30 },
              { label: 'All Categories', value: 100 },
            ]}
          />
        </div>
      </div>

      {/* Chart Section */}
      <Spin spinning={loading}>
        {isEmpty ? (
          <Empty
            description="No data available for the selected filters"
            style={{ padding: '60px 0' }}
          />
        ) : (
          <ResponsiveContainer width="100%" height={600}>
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="category"
                angle={-45}
                textAnchor="end"
                height={100}
                interval={0}
                tick={{ fontSize: 12 }}
              />
              <YAxis
                label={{ value: 'Usage Count', angle: -90, position: 'insideLeft' }}
                tick={{ fontSize: 12 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ paddingTop: '20px' }}
                iconType="rect"
              />
              {toolsList.map((tool, index) => (
                <Bar
                  key={tool}
                  dataKey={tool}
                  stackId="tools"
                  fill={getToolColor(tool, index)}
                  name={tool}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </Spin>
    </Card>
  );
};

export default ToolUsageChart;
